/**
 * LikeButton Component
 * Inline like button that appears next to track names on hover
 * Compact design to fit inline without disrupting layout
 */

import { Heart } from 'lucide-react';
import { useLikeTracks, useUnlikeTracks } from '../../api/hooks/useTracks';

interface LikeButtonProps {
  trackId: string;
  isLiked: boolean;
  className?: string;
}

/**
 * Inline like button - appears next to track name on hover
 * Compact design to fit inline without adding a new column
 */
export function LikeButton({ trackId, isLiked, className = '' }: LikeButtonProps) {
  const likeTracks = useLikeTracks();
  const unlikeTracks = useUnlikeTracks();

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection

    if (isLiked) {
      await unlikeTracks.mutateAsync([trackId]);
    } else {
      await likeTracks.mutateAsync([trackId]);
    }
  };

  const isPending = likeTracks.isPending || unlikeTracks.isPending;

  return (
    <button
      onClick={handleToggle}
      disabled={isPending}
      className={`
        p-0.5 rounded transition-all inline-flex items-center
        ${isLiked
          ? 'text-emerald-500 hover:text-emerald-400 opacity-100'
          : 'text-zinc-500 hover:text-zinc-300'
        }
        ${isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      title={isLiked ? 'Unlike track' : 'Like track'}
      aria-label={isLiked ? 'Unlike track' : 'Like track'}
    >
      <Heart
        className={`w-3.5 h-3.5 ${isLiked ? 'fill-current' : ''}`}
      />
    </button>
  );
}
